import groupBy from "lodash-es/groupBy";
import type {
  Component as VueComponent,
  CreateElement,
  VNode,
  VueConstructor,
  PropType,
} from "vue";
import { defineComponent } from "vue";
import DataCellText from "@/components/controls/grid/renderers/DataCellText.vue";
import { autoComparer } from "@/components/controls/grid/BmGridComparers";
import { stringMatcher } from "@/components/controls/grid/BmGridMatchers";
import { allTheSameAggregator } from "@/components/controls/grid/BmGridAggregators";
import type { BmEnumType } from "@/app/services/baseEnumType";
import type BaseEnumType from "@/app/services/baseEnumType";
import { BmGridState } from "@/components/controls/grid/state/BmGridState";
import BmGridStateRepository from "@/components/controls/grid/state/BmGridStateRepository";
import type { FilterFunc } from "@/components/controls/grid/columnfilters/ColumnFilters";
import { cloneDeep, isEqual } from "lodash-es";
import eventLogger from "@/app/eventlogging/eventLogger";
import { toMap } from "@/app/apiutils";
import type { RawLocation } from "vue-router";

export * from "@/components/controls/grid/state/BmGridState";

export type Comparer = (itemValueA: any, itemValueB: any) => 1 | 0 | -1;
export type Matcher = (itemValue: any, search: string) => boolean;
export type ColumnAlignment = "start" | "center" | "end";
export type Renderer = (
  h: CreateElement,
  value: any,
  item: any,
  context: Renderable
) => VNode;
export type ColumnFooterRenderer = (h: CreateElement) => VNode;
export type GroupSummaryValueExtractor = (
  itemValues: any[],
  groupRowValue?: any,
  parentGroupValues?: string[]
) => any;
export type ColumnFilter = {
  /**
   * Component to render the controls for the filter. Will set properties 'gridState' and 'columnKey'.
   */
  columnFilterComponent: VueConstructor;

  /**
   * Create a filter function from a given filterState
   */
  buildApply: (filterState: any) => FilterFunc;
};

/**
 * Function to transform a vue component with properties matching the renderer function arguments.
 * @param component A vue component.
 */
export function componentToRenderer(
  component: VueConstructor | VueComponent
): Renderer {
  return (
    h: CreateElement,
    value: any,
    item: any,
    context: Renderable
  ): VNode =>
    h(
      component,
      {
        attrs: { value: value, item: item, context: context },
      },
      []
    );
}

export function getGroupRowKey(
  columnId: string,
  groupRowValue: string,
  parentGroupValues: string[] = []
): string {
  // If any are not strings throw an error. Parent group values should already have been checked.
  if (typeof groupRowValue !== "string" || typeof columnId !== "string") {
    eventLogger.logEvent("group-row-key-invalid", {
      columnId,
      groupRowValue,
    });
  }

  return [columnId, ...parentGroupValues, groupRowValue].join("-");
}

export interface SortableAndSearchable {
  comparer: Comparer;
  matcher: Matcher;
  columnFilter?: ColumnFilter;
}

// Formatter for changing the extracted value. For more complex cases you may need to change the renderer.
export interface Formattable {
  formatter: (value: any, item: any) => any;
  csvformatter: (
    value: any,
    item: any,
    stringFormatter: (s: string) => string
  ) => string;
}

export interface RowAction {
  translation: (item: any) => string;
  onClick?: (item: any) => void;
  route?: ((item: any) => RawLocation | undefined) | RawLocation;
  isActive: (item: any) => boolean;
}

export interface RendererMetadata {
  actions?: (RowAction | ((item: any) => RowAction[]))[];
  isAgTruncated?: any;
  textTruncationOptions?: any;
  suffixExtractor?: any;
  prefixExtractor?: any;
  iconSuffixExtractor?: any;
  locationExtractor?: any;
  newTab?: any;
  externalLinkExtractor?: any;
  emailExtractor?: any;
  phoneExtractor?: any;
  editRenderer?: any;
  control?: any;
}

export interface Renderable {
  cardColumnSpan: number;
  align: ColumnAlignment;
  renderer: Renderer;
  rendererMetadata: RendererMetadata;
}

type GridStateMigrator = (opts: {
  gridState: BmGridState;
  columnKey: string;
  columnConfiguration: BmGridColumnConfiguration;
}) => void;

/**
 * Ensure columns that are marked as not removable are not hidden
 */
const removableColumnMigrator: GridStateMigrator = ({
  gridState,
  columnKey,
  columnConfiguration,
}) => {
  if (!columnConfiguration.removable) {
    gridState.removedColumnKeys = gridState.removedColumnKeys.filter(
      (key) => key !== columnKey
    );
  }
};

const gridStateMigrators: GridStateMigrator[] = [removableColumnMigrator];

export class BmGridColumnConfiguration
  implements SortableAndSearchable, Formattable, Renderable
{
  // The title to display on the column header row
  public headingText: string;

  // Extracts the itemValue from the item.
  public extractor: (item: any) => any;
  public classExtractor: (item: any) => string = () => "";

  // Formatter. Note item will be undefined if formatting an aggregate column
  public formatter: (value: any, item: any | undefined) => any = (item) => item;

  // CSV Formatter - default to the same as the normal Formatter but with some string safety
  public csvformatter: (
    value: any,
    item: any | undefined,
    stringFormatter: (s: string) => string
  ) => string = function (
    this: BmGridColumnConfiguration,
    value: any,
    item: any,
    stringFormatter: (s: string) => string
  ) {
    const ob = this.formatter(value, item);
    if (ob instanceof String || typeof ob == "string") {
      return stringFormatter(ob as string);
    }
    return ob?.toString() ?? "";
  };

  // width?
  public shrink: boolean = false;

  // Renderable
  public cardColumnSpan: number = 1;
  public align: ColumnAlignment = "start";
  public renderer: Renderer = componentToRenderer(DataCellText);
  public rendererMetadata: RendererMetadata = {}; // supplemental data to send to the display component

  // Footer
  public footer: ColumnFooterRenderer | null = null;

  // Sort and Search
  public comparer: Comparer = autoComparer;
  public matcher: Matcher = stringMatcher;
  public columnFilter?: ColumnFilter;
  public enumType?: BmEnumType;
  public enumFilter?: (item: BaseEnumType) => boolean;

  // capability
  public sortableAndFilterable: boolean = true;
  public groupable: boolean = true;
  public removable: boolean = true;

  // Grid Controls
  public showInGridControls: boolean = true;

  public extractAndFormat(item: any) {
    return this.formatter(this.extractor(item), item);
  }

  public constructor(
    headingText: string,
    extractor: (item: any) => any,
    options: Partial<BmGridColumnConfiguration>
  ) {
    this.headingText = headingText;
    this.extractor = extractor;
    Object.assign(this, options);
  }
}

export type SummaryExtractor = (
  item: any,
  cols: BmGridColumnConfigurations
) => any;

export enum SummaryPlacement {
  Column,
}

export class BmGridSummaryConfiguration implements Renderable {
  public extractor: SummaryExtractor;
  public groupSummaryValueExtractor: GroupSummaryValueExtractor =
    allTheSameAggregator;
  public placement: SummaryPlacement = SummaryPlacement.Column;

  // The column to place the groupSummaryValueExtractor into if using Column placement
  public columnKey?: string;

  public cardColumnSpan: number = 1;
  public align: ColumnAlignment = "start";
  public renderer: Renderer = componentToRenderer(DataCellText);
  public rendererMetadata: any = {};

  public constructor(
    extractor: SummaryExtractor,
    groupSummaryValueExtractor: GroupSummaryValueExtractor,
    options: Partial<BmGridSummaryConfiguration>
  ) {
    this.extractor = extractor;
    this.groupSummaryValueExtractor = groupSummaryValueExtractor;
    Object.assign(this, options);
  }
}

export type BmGridColumnConfigurations = {
  [columnKey: string]: BmGridColumnConfiguration;
};

export class BmGridConfiguration {
  public defaultGridState?: BmGridState;
  public columns: BmGridColumnConfigurations = {};
  public summaries: BmGridSummaryConfiguration[] = [];
  // TODO - Once itemKey is initialised in every use case make it mandatory and remove
  // itemKey as an option in BmGridData component props.
  public itemKey?: (item: any) => string;
  public title?: string;
}

// Internal Types

type RowType = "content" | "group";
export const isContentRow = (row?: Row): row is ContentRow =>
  row?.rowType === "content";
export const isRowGroup = (row?: Row): row is RowGroup =>
  row?.rowType === "group";

export interface Row {
  // internal state
  isSelected: boolean;
  isExpanded: boolean;
  isVisible: boolean;
  rowType: RowType;
  rowKey: string;
  dropBelow: boolean;
}

export interface ContentRow<T = any> extends Row {
  rowType: "content";
  item: T;
}

export interface RowGroup extends Row {
  rowType: "group";

  // The key of the column this group is grouping on
  columnKey: string;

  // The value from the grouped column this is grouping
  columnValue: string;

  // child rows/groups
  children: Row[];

  // whether this group is a parent of other groups
  isParentOfGroups: boolean;

  // The aggregate values by column key
  aggregateValues: {
    [columnKey: string]: { value: any; summary: BmGridSummaryConfiguration };
  };
}

/**
 * The base component for the grid that is responsible for the data processing only.
 */
export default defineComponent({
  name: "BmGridData",
  model: {
    prop: "selectedItemKeys",
    event: "update:selected-item-keys",
  },
  props: {
    itemKey: {
      type: Function as PropType<(item: any) => string>,
      required: false,
    },
    /**
     * The current user modifiable state of the grid (sorting, grouping, filtering, etc). You can specify the initial
     * state of the grid, and also use this property to load/save state if desired.
     */
    gridState: {
      type: Object as PropType<BmGridState>,
      default: () => BmGridStateRepository.getEphemeral(),
    },
    /**
     * The configuration (types, keys, etc.) of the columns in the grid and the default state (filters).
     */
    gridConfiguration: {
      type: Object as PropType<BmGridConfiguration>,
      required: true,
    },
    /**
     * The array of items to display in the grid. The array is not modified by this grid.
     */
    items: {
      type: Array as PropType<readonly any[]>,
      required: true,
    },
    /**
     * Whether expandable rows are defaulted to expanded.
     */
    defaultExpandExpandable: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether group rows are defaulted to expanded.
     */
    defaultExpandGroups: {
      type: Boolean,
      default: true,
    },
    /**
     * The plural noun of the items displayed in the grid.
     */
    itemNoun: {
      type: String,
      default: "",
    },
    selectedItemKeys: {
      type: Array as PropType<readonly string[]>,
      default: () => [],
    },
    expandedItemKeys: {
      type: Array as PropType<readonly string[]>,
      default: () => [],
    },
    // Only allows one row to be expanded (top-level)
    // will require expanded-item-keys.sync
    accordian: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowSelectable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => true,
    },
    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      virtualGrid: [] as readonly Row[],
      gridStateInternal: new BmGridState() as BmGridState,
      disableReactiveRows: false as boolean,
      hasNonStringOrDuplicateKeys: false as boolean,
    };
  },
  computed: {
    hasGrouping(): boolean {
      return Object.keys(this.computedGroupedColumns).length > 0;
    },
    /**
     * The column definitions from the virtual grid. Rendering should be performed from the column data from this getter.
     * Only includes visisble (not removed) columns
     */
    visibleColumns(): BmGridColumnConfigurations {
      let toRet = {} as BmGridColumnConfigurations;

      Object.entries(this.gridConfiguration.columns)
        // remove grouped
        .filter(
          ([key]) =>
            Object.keys(this.computedGroupedColumns).indexOf(key) === -1
        )
        .filter(
          // remove "removed" columns
          ([key]) => !this.gridState.removedColumnKeys.some((g) => g === key)
        )
        .forEach(([key, col]) => (toRet[key] = col));
      return toRet;
    },
    /**
     * The column definitions from the virtual grid. Row filtering should be performed from these columns
     * Only includes columns with filters applied
     */
    filterableColumns(): BmGridColumnConfigurations {
      let toRet = {} as BmGridColumnConfigurations;

      Object.entries(this.gridConfiguration.columns)
        // remove grouped
        .filter(
          ([key]) =>
            Object.keys(this.computedGroupedColumns).indexOf(key) === -1
        )
        .filter(
          // Remove all columns that don't have filters
          ([key]) => this.gridState.columnFilters[key]
        )
        .forEach(([key, col]) => (toRet[key] = col));
      return toRet;
    },
    /**
     * Get the columns that are used in the groupings. The order of the keys defines the grouping level.
     */
    computedGroupedColumns(): BmGridColumnConfigurations {
      return Object.fromEntries(
        Object.entries(this.gridConfiguration.columns)
          .filter(([, value]) => value.groupable)
          .filter(([key]) => this.gridState?.groupBy.indexOf(key) !== -1)
      );
    },
    /**
     * Get the columns that are used for sorting. The order of the keys defines the sorting priority.
     * The returned columns have an augmented variable 'sortDescending' to indicate direction.
     */
    computedSortableColumns(): {
      [columnKey: string]: BmGridColumnConfiguration & {
        sortDescending?: boolean;
      };
    } {
      return Object.fromEntries(
        this.gridState.sortBy
          .map((colMatcher) => {
            const value = this.gridConfiguration.columns[colMatcher.columnKey];
            if (value === undefined) {
              /* eslint-disable no-console */
              console.error(
                `sort defined on column ${colMatcher.columnKey}, but column does not exist`
              );
              return null;
            }
            if (!value.sortableAndFilterable) {
              return null;
            }
            return [
              colMatcher.columnKey,
              Object.assign({ sortDescending: colMatcher?.descending }, value),
            ];
          })
          .filter((x) => x != null) as any[]
      );
    },
    currentRowsMapWithIndex() {
      return toMap(
        this.getFlattenedRows(this.virtualGrid).map(
          (row, idx) => [row, idx] as [Row, number]
        ),
        ([row]) => row.rowKey + row.rowType
      );
    },
  },
  created() {
    // Apply migrations to grid state
    gridStateMigrators.forEach((gridStateMigrator) =>
      Object.entries(this.gridConfiguration.columns).forEach(
        ([columnKey, columnConfiguration]) =>
          gridStateMigrator({
            gridState: this.gridState,
            columnKey,
            columnConfiguration,
          })
      )
    );

    this.gridStateInternal = cloneDeep(this.gridState);

    // Watch the grid state property and validate it before updating internal state.
    this.$watch("gridState", this.updateGridState, {
      immediate: true,
      deep: true,
    });

    // Create a watcher than reloads the grid if the key properties change.
    ["columns", "gridStateInternal", "selectedItemKeys"].forEach((prop) =>
      this.$watch(prop, this.refreshVirtualGrid, { deep: true })
    );

    [
      "defaultExpandExpandable",
      "defaultExpandGroups",
      "expandedItemKeys",
    ].forEach((prop) =>
      this.$watch(prop, () => this.refreshVirtualGrid(false))
    );

    this.$watch(
      "items",
      (newItems: any[], oldItems: any[]) => {
        this.refreshVirtualGrid(true);
        this.checkItemsForDuplicateOrUndefinedKeys(newItems, oldItems);
      },
      { deep: true }
    );

    this.refreshVirtualGrid(false);
  },
  methods: {
    extractKey(item: any) {
      return (this.gridConfiguration.itemKey ?? this.itemKey)?.(item) ?? "";
    },

    /** Temporary solution to check for duplicate or undefined keys*/
    checkItemsForDuplicateOrUndefinedKeys(newItems: any[], oldItems: any[]) {
      if (
        oldItems.length !== 0 ||
        !newItems.length ||
        this.hasNonStringOrDuplicateKeys
      ) {
        return;
      }

      const uniqueKeys = new Set();
      let invalidKeyReason = "";

      const hasInvalidKeys = newItems.some((item) => {
        const key = this.extractKey(item);
        if (key === undefined) {
          invalidKeyReason = "undefined";
          return true;
        }
        if (typeof key !== "string") {
          invalidKeyReason = "non_string";
          return true;
        }
        if (uniqueKeys.has(key)) {
          invalidKeyReason = "duplicate";
          return true;
        }
        uniqueKeys.add(key);
      });

      if (hasInvalidKeys) {
        this.hasNonStringOrDuplicateKeys = true;
        eventLogger.logEvent("grid_keys" + invalidKeyReason, {}); // Just log the event, need only context of the page
      }
    },
    setDisableReactiveRows(value: boolean) {
      this.disableReactiveRows = value;
      if (!value) {
        this.refreshVirtualGrid();
      }
    },
    toggleSelectedRow(row: Row | undefined) {
      let selectedItems = new Set(this.selectedItemKeys);

      if (row) {
        if (row.isSelected || !this.rowSelectable(row)) {
          selectedItems.delete(row?.rowKey);
        } else {
          selectedItems.add(row?.rowKey);
        }
      } else {
        // it was the header cell
        const isAllSelected = this.virtualGrid.every(
          (row) => row.isSelected || !this.rowSelectable(row)
        );

        if (isAllSelected) {
          const visibleSelectedRows = this.virtualGrid.filter((row) =>
            selectedItems.has(row.rowKey)
          );

          visibleSelectedRows.forEach((row) => {
            selectedItems.delete(row.rowKey);
          });
        } else {
          const visibleSelectableRows = this.virtualGrid.filter((row) =>
            this.rowSelectable(row)
          );

          visibleSelectableRows.forEach((row) => {
            selectedItems.add(row.rowKey);
          });
        }
      }

      this.$emit("update:selected-item-keys", [...selectedItems.values()]);
    },
    /**
     * A flat view (ContentRow and RowGroup in a 2D array).
     */
    getFlattenedRows(rootNode: readonly Row[]): Row[] {
      const recursiveCrawler = (rows: readonly Row[]) => {
        let returnRows: Row[] = [];
        for (const row of rows) {
          returnRows.push(row);
          if (row.rowType === "group") {
            returnRows = returnRows.concat(
              recursiveCrawler((row as RowGroup).children)
            );
          }
        }

        return returnRows;
      };

      return recursiveCrawler(rootNode);
    },
    countGroupedRows() {
      if (Object.keys(this.computedGroupedColumns).length == 0) {
        return this.items.length;
      } else {
        const groupColumnKey = Object.keys(this.computedGroupedColumns)[0];
        const valueExtractor =
          this.gridConfiguration.columns[groupColumnKey].extractor;
        return new Set(this.items.map(valueExtractor)).size;
      }
    },
    /**
     * Recursive grouping function. Breaks the virtual grid down into their specified groups.
     */
    calculateRowGroups(
      rows: ContentRow[],
      groupIndex = 0,
      reapplyRowStates: boolean = true,
      parentGroupValues: string[] = []
    ): RowGroup[] {
      let groupRows = [];
      const groupColumnKey = Object.keys(this.computedGroupedColumns)[
        groupIndex
      ];
      const valueExtractor =
        this.gridConfiguration.columns[groupColumnKey].extractor;

      // @TODO: [BREW-4732] Grouping on a nullish value should resolve to a placeholder

      // This is a bit on a blunt instrument as it simply toStrings the extracted value in the item to determine the
      //  groups. Ideally this should use the comparers on the columns - although performance will suffer.
      const groupByValue = groupBy(rows, (row) => valueExtractor(row.item));

      for (const entry of Object.entries(groupByValue)) {
        let childRows: Row[] | RowGroup[] = [];
        const childGroup = groupIndex === this.gridState.groupBy.length - 1;

        if (!childGroup) {
          childRows = this.calculateRowGroups(
            entry[1],
            groupIndex + 1,
            reapplyRowStates,
            [...parentGroupValues, entry[0]]
          );
        } else {
          childRows = entry[1];
        }

        const childItems = this.getFlattenedRows(childRows)
          .filter((r) => r.rowType === "content")
          .map((r) => (r as ContentRow).item);

        const aggregateValues: {
          [key: string]: { value: any; summary: BmGridSummaryConfiguration };
        } = {};

        // Calculate aggregates
        for (const summary of this.gridConfiguration.summaries) {
          const extractedValues = childItems.map((item) =>
            summary.extractor(item, this.gridConfiguration.columns)
          );

          if (summary.placement === SummaryPlacement.Column) {
            if (
              !summary.columnKey ||
              !Object.prototype.hasOwnProperty.call(
                this.gridConfiguration.columns,
                summary.columnKey
              )
            ) {
              throw new Error(
                "Summary with column placement must specify a column key to an existing column."
              );
            }
            aggregateValues[summary.columnKey] = {
              value: summary.groupSummaryValueExtractor(
                extractedValues,
                entry[0],
                parentGroupValues
              ),
              summary: summary,
            };
          }
        }
        const groupRowKey = getGroupRowKey(
          groupColumnKey,
          entry[0],
          parentGroupValues
        );

        // try to retrieve existing rowgroup to reapply expansion state.
        const existingRow = reapplyRowStates
          ? this.currentRowsMapWithIndex.get(groupRowKey + "group")
          : undefined;

        groupRows.push({
          rowKey: groupRowKey,
          columnKey: groupColumnKey,
          columnValue: entry[0],
          get isSelected() {
            return childRows.every((r: Row | RowGroup) => r.isSelected);
          },
          set isSelected(value: boolean) {
            childRows.forEach((r: Row | RowGroup) => (r.isSelected = value));
          },
          isExpanded: existingRow
            ? existingRow[0].isExpanded
            : this.defaultExpandGroups,
          children: childRows,
          isParentOfGroups: !childGroup,
          rowType: "group",
          aggregateValues,
        } as RowGroup);
      }

      return groupRows;
    },
    /**
     * Performs a full refresh of the virtual grid, will cause the grid to re-render and internal state will be lost.
     */
    refreshVirtualGrid(reapplyRowStates: boolean = true) {
      if (this.disableReactiveRows) {
        return;
      }

      const selectedKeysSet = new Set(this.selectedItemKeys);

      // Create row objects from items. We try and find an existing row using the item key and rank the new items
      // accordingly. The sort function before we return ensures existing items remain in the same order as before.

      let newGrid: Row[] = [];

      // We only want to map-in out original row indexes if we're sorting
      // sort & map will still take time, and are pointless if we don't care
      if (this.allowSort) {
        newGrid = this.items
          .map((i: any) => {
            const rowKey = this.extractKey(i);
            const existingRow = reapplyRowStates
              ? this.currentRowsMapWithIndex.get(rowKey + "content")
              : undefined;

            return [
              existingRow ? existingRow[1] : -1,
              {
                rowKey: rowKey,
                item: i,
                isExpanded: existingRow
                  ? existingRow[0].isExpanded
                  : this.defaultExpandExpandable ||
                    this.expandedItemKeys.indexOf(rowKey) > -1,
                isSelected: selectedKeysSet.has(rowKey),
                rowType: "content",
                dropBelow: false,
              } as ContentRow,
            ];
          })
          .sort((a, b) => {
            return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
          })
          .map(([_, row]) => row as ContentRow);
      } else {
        newGrid = this.items.map((i: any) => {
          const rowKey = this.extractKey(i);
          const existingRow = reapplyRowStates
            ? this.currentRowsMapWithIndex.get(rowKey + "content")
            : undefined;

          return {
            rowKey: rowKey,
            item: i,
            isExpanded: existingRow
              ? existingRow[0].isExpanded
              : this.defaultExpandExpandable ||
                this.expandedItemKeys.indexOf(rowKey) > -1,
            isSelected: selectedKeysSet.has(rowKey),
            rowType: "content",
            dropBelow: false,
          } as ContentRow;
        });
      }

      // grid search filter
      // filter on all visible columns including those that are grouped
      if (this.gridState.gridSearchFilter) {
        const searchString = this.gridState.gridSearchFilter;
        newGrid = (newGrid as ContentRow[]).filter((row) =>
          Object.values({
            ...this.visibleColumns,
            ...this.computedGroupedColumns,
          }).some((col) => col.matcher(col.extractor(row.item), searchString))
        );
      }

      // Column Filters
      newGrid = applyColumnFilters(
        newGrid as ContentRow[],
        {
          ...this.filterableColumns,
          ...this.computedGroupedColumns,
        },
        this.gridState.columnFilters
      );

      // Group the rows
      if (Object.keys(this.computedGroupedColumns).length > 0) {
        newGrid = this.calculateRowGroups(
          newGrid as ContentRow[],
          0,
          reapplyRowStates
        ) as Row[];
      }

      // Sort in priority order
      // Don't sort if we don't allow it, our items are normally
      // already in the order we want them to be
      if (
        Object.keys(this.computedSortableColumns).length > 0 &&
        this.allowSort
      ) {
        applySorting(newGrid, this.computedSortableColumns);
      }

      this.$emit(
        "filter-summary",
        `${newGrid.length} / ${this.countGroupedRows()} ${this.itemNoun}`
      );

      this.virtualGrid = Object.freeze(newGrid);
    },
    updateGridState(): void {
      // Validate the requested state and reset it if it contains columns that are
      // not valid.
      if (!this.gridState.useGridDefault) {
        const availableColumnKeys = new Set(
          Object.keys(this.gridConfiguration.columns)
        );
        const referencedColumnKeys = new Set(
          this.gridState.groupBy
            .concat(this.gridState.sortBy.map((sb) => sb.columnKey))
            .concat(Object.keys(this.gridState.columnFilters))
        );

        // Check the requested state referenced keys are all valid. There is more
        // validation that could be done here but this should be sufficient for
        // the more common sync issues.
        if (
          [...referencedColumnKeys].some(
            (columnKey) => !availableColumnKeys.has(columnKey)
          ) ||
          // Requested sort on column that is not sortable
          this.gridState.sortBy.some(
            (sb) =>
              !this.gridConfiguration.columns[sb.columnKey]
                .sortableAndFilterable
          ) ||
          // Requested filter on column that is not filterable
          Object.keys(this.gridState.columnFilters).some(
            (columnKey) =>
              !this.gridConfiguration.columns[columnKey].sortableAndFilterable
          )
        ) {
          // the supplied state is invalid so clear it - technically this is mutating
          // a prop that ideally we should avoid but this is much easier than wiring
          // up 'sync' everywhere.
          this.gridState.reset();
        }
      }

      if (this.gridState.useGridDefault) {
        const stateId = this.gridState.stateId;
        // Clone deep used as object.assign uses a shallow copy.
        // This caused defaultGridState to point to the same object as this.gridState
        Object.assign(
          this.gridState,
          cloneDeep(this.gridConfiguration.defaultGridState)
        );
        // eslint-disable-next-line vue/no-mutating-props
        this.gridState.stateId = stateId;

        // eslint-disable-next-line vue/no-mutating-props
        this.gridState.useGridDefault = false;
      }

      // Do not update the grid unless the state is different to current.
      if (!isEqual(this.gridState, this.gridStateInternal)) {
        this.gridStateInternal = cloneDeep(this.gridState);
        // Save any modified grid state information
        BmGridStateRepository.save();
      }
    },
  },
});

/**
 * Fitlers a flat (ContentRows only) grid by the column filters.
 * @param flatGrid The grid to filter.
 * @param columns The columns to apply the filters from.
 * @param columnFilterState The full column filter state object from the gridState.
 */
function applyColumnFilters(
  flatGrid: ContentRow[],
  columns: BmGridColumnConfigurations,
  columnFilterState: { [columnKey: string]: any }
): ContentRow[] {
  for (const [columnKey, columnDef] of Object.entries(columns).filter(
    ([_, col]) => col.columnFilter !== undefined
  )) {
    // Apply each column filter in any order
    let filterState = columnFilterState[columnKey];

    // If there is no state for a column filter it is deemed inactive
    if (filterState === undefined) {
      continue;
    }

    let columnFilter = columnDef.columnFilter;

    if (columnFilter !== undefined) {
      let filterFunction = columnFilter.buildApply(filterState);
      flatGrid = flatGrid.filter((row) =>
        filterFunction(
          columnDef.extractor(row.item),
          columnDef.extractAndFormat(row.item)
        )
      );
    }
  }

  return flatGrid;
}

/**
 * Sorts a flat or nested grid (RowGroup and ContentRow tree).
 */
function applySorting(
  flatOrGroupedGrid: Row[],
  sortBy: {
    [columnKey: string]: BmGridColumnConfiguration & {
      sortDescending?: boolean;
    };
  }
) {
  const recursiveGroupSorter = (rows: Row[], depth: number = 0) => {
    if (!rows.length) {
      return;
    }

    if (rows[0].rowType === "content") {
      // List of contents rows - so sort by criteria one by one
      (rows as ContentRow[]).sort((a, b) => {
        for (const [sortColumnKey, sortColumn] of Object.entries(sortBy)) {
          if (!sortColumn.sortableAndFilterable) {
            continue;
          }

          const valA = sortColumn.extractor(a.item);
          const valB = sortColumn.extractor(b.item);
          const sortVal = sortColumn.comparer(valA, valB);
          if (sortVal !== 0) {
            return sortVal * (sortColumn.sortDescending ? -1 : 1);
          }
        }

        return 0;
      });
    } else if (rows[0].rowType === "group") {
      // List of group rows
      (rows as RowGroup[]).sort((a, b) => {
        for (const [sortColumnKey, sortColumn] of Object.entries(sortBy)) {
          if (!sortColumn.sortableAndFilterable) {
            continue;
          }

          let sortVal = 0;

          if (a.columnKey === sortColumnKey) {
            sortVal = sortColumn.comparer(a.columnValue, b.columnValue);
          } else {
            // otherwise we check to see if we have an aggregated value for this group and sort on that
            if (
              Object.prototype.hasOwnProperty.call(
                a.aggregateValues,
                sortColumnKey
              )
            ) {
              sortVal = sortColumn.comparer(
                a.aggregateValues[sortColumnKey].value,
                b.aggregateValues[sortColumnKey].value
              );
            }
          }

          if (sortVal !== 0) {
            return sortVal * (sortColumn.sortDescending ? -1 : 1);
          }
        }

        return 0;
      });

      for (const row of rows as RowGroup[]) {
        recursiveGroupSorter(row.children, depth + 1);
      }
    }
  };

  // start recursive sort
  recursiveGroupSorter(flatOrGroupedGrid, 0);
}
